<template>
  <Simulation
    v-if="smallScreen"
    class="block mb-4"
    :price="product.price"
    :productCategoryValue="category"
    :offeredMonths="offeredMonths"
    :contract-type="contractType"
    :productless="productless"
  />
  <VeeForm v-slot="{ handleSubmit }" :validation-schema="formSchema" as="div" ref="subscriptionForm">
    <form @submit="handleSubmit($event, submitForm)">
      <div class="sm:inline lg:flex">
        <div class="lg:w-4/5 lg:mr-4">
          <SellerForm :sellerCompleteName="sellerCompleteName" @selectedSeller="handleSelectedSeller" />
          <CategorySelector @selectProductCategory="handleSelectProductCategory" :categories="categories" />
          <div v-if="category">
            <ProductForm
              v-if="productRequired"
              v-model="product"
              :category="category"
              :isBikeCategory="isBikeCategory"
            />
            <component
              :is="getCustomerFormComponent"
              v-model="customer"
              :address2="address.address2"
              :addressError="address.addressError"
              @update:address2="address.address2 = $event"
              @newAddress="newAddress"
            />
          </div>
        </div>
        <div class="lg:w-2/5 mt-4 lg:mt-0">
          <div v-if="bigScreen">
            <Retributions v-if="showRetributions" class="mb-4" />
          </div>
          <div v-if="category || bigScreen">
            <Simulation
              :price="product.price"
              :productCategoryValue="category"
              :offeredMonths="offeredMonths"
              :contract-type="contractType"
              :productless="productless"
            />
            <ContractDetailsSection class="mt-4" :categories="categories" :productCategoryValue="category" />
          </div>
          <FormButtons
            v-if="category"
            :isLeadForm="isLeadForm"
            @sendTextMessage="handleSendTextMessage"
            @paymentInScreen="handlePaymentInScreen"
            @recordASale="handleRecordASale"
          />
        </div>
      </div>
    </form>
  </VeeForm>
  <CustomerFormShortDetailsModal
    v-if="isLeadForm"
    :open="openCustomerFormShortDetailsModal"
    @closeCustomerFormShortDetailsModal="openCustomerFormShortDetailsModal = false"
    @submitCustomerFormShortDetailsModal="handleSubmitCustomerFormShortDetailsModal"
  />
  <SaleCreation
    :isOpen="isSaleCreationOpen"
    :saleRecording="saleRecording"
    :customer="customer"
    :product="product"
    :address="address"
    :category="category"
    :sellerId="subscription.sellerId"
    :contractType="contractType"
    @closeSaleModal="handleCloseSaleModal"
  />
  <OfferPayment
    :isOpen="isOfferPaymentOpen"
    :lead="subscription"
    :product="product"
    :customer="customer"
    @sendFailedPaymentLink="handleSendFailedPaymentLink"
    @closePaymentModal="handleClosePaymentModal"
    @updateOffer="handleUpdateOffer"
  />
</template>

<script>
import { Form as VeeForm } from "vee-validate";
import {
  minimalFormSchema,
  basicFormSchema,
  minimalFormSchemaWithoutProduct,
  basicFormSchemaWithoutProduct,
  professionalCustomerDetailsSchema,
} from "@/components/pos/subscription/utils/validation";
import { mapActions, mapState, mapGetters } from "vuex";

import SellerForm from "@/components/pos/subscription/form/SellerForm.vue";
import ProductForm from "@/components/pos/subscription/form/ProductForm.vue";
import CustomerFormShort from "@/components/pos/subscription/form/CustomerFormShort.vue";
import CustomerFormLong from "@/components/pos/subscription/form/CustomerFormLong.vue";
import CustomerFormMedium from "@/components/pos/subscription/form/CustomerFormMedium.vue";
import CategorySelector from "@/components/selector/CategorySelector.vue";
import FormButtons from "@/components/pos/subscription/form/FormButtons.vue";
import Simulation from "@/components/pos/subscription/form/Simulation.vue";
import ContractDetailsSection from "@/components/pos/subscription/form/ContractDetailsSection.vue";
import CustomerFormShortDetailsModal from "@/components/pos/subscription/form/CustomerFormShortDetailsModal.vue";
import OfferPayment from "@/components/pos/subscription/payment/OfferPayment.vue";
import SaleCreation from "@/components/pos/subscription/salecreation/SaleCreation.vue";

import LeadsService from "@/api/services/leads";
import SaleService from "@/api/services/sale";

import { EventBus } from "@/utils/event_bus";
import { screenSize, screens } from "@/utils/breakpoints.js";
import { toCamelCase, isPresent } from "@estaly/ui";
import _ from "lodash";
import Retributions from "@/components/pos/subscription/form/Retributions.vue";
import ProfessionalCustomerForm from "@/components/pos/subscription/form/ProfessionalCustomerForm.vue";

export default {
  components: {
    ProfessionalCustomerForm,
    Retributions,
    VeeForm,
    SellerForm,
    CategorySelector,
    ProductForm,
    CustomerFormShort,
    CustomerFormLong,
    CustomerFormMedium,
    FormButtons,
    ContractDetailsSection,
    Simulation,
    CustomerFormShortDetailsModal,
    OfferPayment,
    SaleCreation,
  },
  props: {
    categories: {
      type: Array,
      required: true,
    },
    offeredMonths: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      screens,
      subscription: {
        id: "",
        estalyId: "",
        uniqueToken: "",
        subscriptionLink: "",
        offers: [],
        discount: "",
        product: {
          title: "",
          price: "",
          referenceId: "",
        },
        category: null,
        customer: {
          email: "",
          phone: "",
          firstName: "",
          lastName: "",
          birthDate: "",
          birthCity: "",
          birthCountry: "",
          professionalCustomer: false,
          sirenNumber: "",
          legalForm: "",
          buyerRole: "",
          companyName: "",
        },
        address: {
          address1: "",
          address2: "",
          city: "",
          province: "",
          country: "",
          zipCode: "",
          addressError: false,
        },
        invitationSentByEstaly: true,
        paymentInStorePlatform: false,
        sellerId: "",
      },
      openCustomerFormShortDetailsModal: false,
      recordASale: false,
      isSaleCreationOpen: false,
      isOfferPaymentOpen: false,
      saleRecording: {
        contractDetails: [],
        monthlyPrice: "",
      },
      sellerCompleteName: "",
      contractType: null,
      productless: null,
      saleFormType: null,
    };
  },

  computed: {
    ...mapState("store", ["selectedStoreId"]),
    ...mapGetters("store", ["selectedStore"]),
    ...mapGetters("auth", ["currentUserHasPermission", "isSellerOrResponsible"]),

    getCustomerFormComponent() {
      if (this.professionalCustomer) {
        return "ProfessionalCustomerForm";
      } else if (this.isLeadForm) {
        return "CustomerFormShort";
      } else if (this.isSaleWithoutBirthInfoForm) {
        return "CustomerFormMedium";
      }
      return "CustomerFormLong";
    },

    professionalCustomer() {
      return this.customer.professionalCustomer;
    },

    storeFeatureConfiguration() {
      return this.selectedStore?.storeFeatureConfiguration;
    },

    isLeadForm() {
      return this.saleFormType === "lead";
    },

    isSaleWithoutBirthInfoForm() {
      return this.saleFormType === "sale_without_birth_info";
    },

    formSchema() {
      if (this.professionalCustomer) {
        return professionalCustomerDetailsSchema;
      } else if (this.isLeadForm || this.isSaleWithoutBirthInfoForm) {
        return this.productRequired ? minimalFormSchema : minimalFormSchemaWithoutProduct;
      } else {
        return this.productRequired ? basicFormSchema : basicFormSchemaWithoutProduct;
      }
    },

    product: {
      get() {
        return this.subscription.product;
      },
      set(value) {
        this.subscription.product = value;
      },
    },

    category: {
      get() {
        return this.subscription.category;
      },
      set(value) {
        this.subscription.category = value;
      },
    },

    customer: {
      get() {
        return this.subscription.customer;
      },
      set(value) {
        this.subscription.customer = value;
      },
    },

    address: {
      get() {
        return this.subscription.address;
      },
      set(value) {
        this.subscription.address = value;
      },
    },

    isBikeCategory() {
      return this.category === "bike";
    },

    currentScreenSize() {
      return screenSize();
    },

    bigScreen() {
      return this.currentScreenSize >= screens.lg;
    },

    smallScreen() {
      return !this.bigScreen;
    },

    productRequired() {
      return !this.productless;
    },

    showRetributions() {
      return this.isSellerOrResponsible && this.currentUserHasPermission("retributions");
    },
  },

  watch: {
    category() {
      this.customer.professionalCustomer = false;
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("store", ["withLoader"]),

    handleSelectedSeller(seller) {
      this.sellerCompleteName = seller ? seller.name : "";
      this.subscription.sellerId = seller ? seller.id : "";
    },

    handleSelectProductCategory(selectedProductCategory) {
      this.product = {
        title: "",
        price: "",
        referenceId: "",
      };
      this.saleFormType = selectedProductCategory?.saleFormType;
      this.category = selectedProductCategory?.productCategoryName;
      this.contractType = selectedProductCategory?.contractType;
      this.productless = selectedProductCategory?.contractDistribution?.productless ?? false;

      // Reset form validation to avoid flash of validation errors
      this.$nextTick(() => {
        this.$refs.subscriptionForm?.resetForm();
      });
    },

    handleRecordASale() {
      this.recordASale = true;
    },

    handleSendTextMessage() {
      this.subscription.invitationSentByEstaly = true;
    },

    handlePaymentInScreen() {
      this.subscription.invitationSentByEstaly = false;
      this.subscription.paymentInStorePlatform = true;
    },

    handleCloseSaleModal(resetAttributes) {
      if (resetAttributes) {
        this.resetAttributes();
      } else {
        this.isSaleCreationOpen = false;
      }
    },

    handleClosePaymentModal(resetAttributes) {
      if (resetAttributes) {
        this.resetAttributes();
      } else {
        this.isOfferPaymentOpen = false;
      }
    },

    handleUpdateOffer({ index, offer }) {
      this.subscription.offers = [
        ...this.subscription.offers.slice(0, index),
        offer,
        ...this.subscription.offers.slice(index + 1),
      ];
    },

    async handleSendFailedPaymentLink() {
      this.subscription.invitationSentByEstaly = true;
      this.subscription.paymentInStorePlatform = false;
      await this.submitForm();
    },

    async submitForm() {
      if (this.isLeadForm) {
        if (this.recordASale) {
          this.openCustomerFormShortDetailsModal = true;
        } else {
          await this.handleLeadCreation();
        }
      } else {
        if (isPresent(this.address.address1) && isPresent(this.address.city) && isPresent(this.address.country)) {
          if (this.recordASale) {
            await this.handleSaleRecording();
          } else {
            await this.handleLeadCreation();
          }
        } else {
          this.address.addressError = true;
        }
      }
    },

    async handleLeadCreation() {
      const params = {
        lead: _.pick(this.subscription, [
          "customer",
          "address",
          ...(this.productRequired ? ["product"] : []),
          "invitationSentByEstaly",
          "paymentInStorePlatform",
          "sellerId",
          "category",
        ]),
      };

      await this.withLoader(async () => {
        const response = await LeadsService.create(this.selectedStoreId, params);
        if (!response?.success) return;
        const data = toCamelCase(response.data);

        if (this.subscription.paymentInStorePlatform) {
          Object.assign(this.subscription, {
            id: data.lead.id,
            estalyId: data.lead.estalyId,
            offers: data.lead.offers,
            discount: data.lead.discount,
          });
          if (this.isLeadForm) {
            this.openCustomerFormShortDetailsModal = true;
          } else {
            this.isOfferPaymentOpen = true;
          }
          this.subscription.paymentInStorePlatform = false;
        } else {
          this.notify({
            category: "simple",
            type: "success",
            title: this.isLeadForm ? "Devis envoyé" : "Lien de paiement envoyé",
            text: "Envoyé au client par SMS et par e-mail.",
          });
          this.resetAttributes();
        }
      });
    },

    async handleSubmitCustomerFormShortDetailsModal(params) {
      if (this.recordASale) {
        this.subscription.customer = {
          ...this.subscription.customer,
          ...params.lead.customer,
        };
        this.address = params.lead.address;
        this.openCustomerFormShortDetailsModal = false;
        await this.handleSaleRecording();
      } else {
        const response = await LeadsService.update(this.subscription.estalyId, params);
        if (response?.success) {
          this.openCustomerFormShortDetailsModal = false;
          this.isOfferPaymentOpen = true;
        }
      }
    },

    async handleSaleRecording() {
      await this.withLoader(async () => {
        const response = await SaleService.seeContractDetails(this.category, this.product.price, this.selectedStoreId);

        if (!response?.success) return;
        const data = toCamelCase(response.data);

        this.saleRecording.contractDetails = data.contracts.filter((contract) => !contract.recurring);
        this.monthlyPrice = (parseFloat(this.saleRecording.contractDetails[0].price) / 12).toFixed(2);
      });

      this.isSaleCreationOpen = true;
      this.recordASale = false;
    },

    newAddress(value) {
      this.address = value;
      if (isPresent(this.address.address1) && isPresent(this.address.city) && isPresent(this.address.country)) {
        this.address.addressError = false;
      }
    },

    resetAttributes() {
      this.subscription = {
        id: "",
        estalyId: "",
        uniqueToken: "",
        subscriptionLink: "",
        offers: [],
        discount: "",
        product: {
          title: "",
          price: "",
          referenceId: "",
        },
        category: null,
        customer: {
          email: "",
          phone: "",
          firstName: "",
          lastName: "",
          birthDate: "",
          birthCity: "",
          birthCountry: "",
        },
        address: {
          address1: "",
          address2: "",
          city: "",
          province: "",
          country: "",
          zipCode: "",
          addressError: false,
        },
        invitationSentByEstaly: true,
        paymentInStorePlatform: false,
        sellerId: "",
      };
      this.saleRecording = {
        contractDetails: [],
        monthlyPrice: "",
      };

      this.isSaleCreationOpen = false;
      this.openCustomerFormShortDetailsModal = false;
      this.isOfferPaymentOpen = false;
      this.recordASale = false;
      this.sellerCompleteName = "";
      this.contractType = null;
      this.productless = null;
      this.saleFormType = null;

      EventBus.emit("resetAddress");
      EventBus.emit("resetSelectedProductCategory");
      this.$refs.subscriptionForm.resetForm();
    },
  },
};
</script>
